import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Moon, Star } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const PRIVACY_CONTENT = [{
  title: 'Information Collection',
  content: 'We collect information you provide directly to us when you purchase the Bedtime Pass Kit, including your name, email address, and payment information. We do not store credit card details on our servers; payments are processed securely by our third-party payment processors.'
}, {
  title: 'Use of Information',
  content: 'We use your information to deliver the digital download links to your email, communicate with you about your order, and send you important updates about the product. We do not sell your personal data to third parties.'
}, {
  title: 'Digital Delivery',
  content: 'Since this is a digital product, delivery is immediate via email. We use your email address solely for the purpose of delivering your purchase and providing customer support.'
}, {
  title: 'Cookies',
  content: 'We use cookies to help our site run effectively and to provide the best experience. These small data files allow us to remember your preferences and understand how you use our site.'
}, {
  title: 'Data Protection',
  content: 'We implement a variety of security measures to maintain the safety of your personal information. Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights.'
}];
const TERMS_CONTENT = [{
  title: 'Digital Product Policy',
  content: 'The Bedtime Pass Kit is a digital download (PDF). No physical product will be shipped. Upon purchase, you will receive an email with a link to download your files immediately.'
}, {
  title: 'License & Usage',
  content: 'Your purchase grants you a single personal license to print and use the materials for your own family. You may not redistribute, resell, share, or reproduce these files for commercial use or for other families without express written permission.'
}, {
  title: 'Refund Policy',
  content: 'We offer a 100% money-back guarantee. If the system does not work for your family after giving it a fair try, please contact us for a full refund. We want you to be happy with your purchase.'
}, {
  title: 'Disclaimer',
  content: 'This kit is an educational resource for behavioral management of bedtime issues. It is not medical advice and is not a substitute for professional medical or psychological diagnosis or treatment. If you suspect your child has a medical sleep disorder, please consult a pediatrician.'
}, {
  title: 'Intellectual Property',
  content: 'All content, designs, text, and graphics in the Bedtime Pass Kit are the intellectual property of the creator and are protected by copyright laws.'
}];
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeModal]);
  const content = activeModal === 'privacy' ? PRIVACY_CONTENT : TERMS_CONTENT;
  const title = activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service';
  return <>
      <footer className="w-full bg-[#faf6ef] border-t border-[#c9a96e]/20 py-12 pb-32 px-6 text-center relative z-10" data-id="element-41">
        <div className="max-w-[680px] mx-auto flex flex-col items-center" data-id="element-42">
          {/* Decorative Icon */}
          <div className="text-[#c9a96e] mb-6 opacity-60" data-id="element-43">
            <Moon size={20} className="inline-block" data-id="element-44" />
          </div>

          {/* Links */}
          <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4 text-sm md:text-base font-medium mb-6" data-id="element-45">
            <button onClick={() => setActiveModal('privacy')} className="text-[#6b7fa3] hover:text-[#1e2a4a] transition-colors duration-200 border-b border-transparent hover:border-[#1e2a4a]/20" data-id="element-46">
              Privacy Policy
            </button>
            <span className="text-[#c9a96e]/40" data-id="element-47">•</span>
            <button onClick={() => setActiveModal('terms')} className="text-[#6b7fa3] hover:text-[#1e2a4a] transition-colors duration-200 border-b border-transparent hover:border-[#1e2a4a]/20" data-id="element-48">
              Terms of Service
            </button>
          </div>

          {/* Copyright */}
          <p className="text-xs text-[#6b7fa3]/60 font-serif italic" data-id="element-49">
            © 2026 Bedtime Pass Kit. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-50">
        {activeModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-[#1e2a4a]/60 backdrop-blur-sm z-[100]" data-id="element-51" />

            {/* Modal Container */}
            <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none" data-id="element-52">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} className="bg-[#faf6ef] w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden pointer-events-auto relative border border-[#c9a96e]/20 flex flex-col max-h-[85vh]" data-id="element-53">
                {/* Close Button */}
                <button onClick={() => setActiveModal(null)} className="absolute top-4 right-4 p-2 text-[#6b7fa3] hover:text-[#faf6ef] hover:bg-[#1e2a4a]/10 rounded-full transition-colors z-20" aria-label="Close popup" data-id="element-54">
                  <X size={24} data-id="element-55" />
                </button>

                {/* Header */}
                <div className="bg-[#1e2a4a] p-6 text-center relative shrink-0" data-id="element-56">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#c9a96e]/0 via-[#c9a96e] to-[#c9a96e]/0 opacity-50" data-id="element-57" />
                  <h2 className="text-xl md:text-2xl font-serif font-bold text-[#faf6ef]" data-id="element-58">
                    {title}
                  </h2>
                  <p className="text-[#c9a96e] text-xs mt-2 font-serif italic opacity-80" data-id="element-59">
                    Last updated: February 2026
                  </p>
                </div>

                {/* Scrollable Content */}
                <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar" data-id="element-60">
                  <div className="space-y-8" data-id="element-61">
                    {content.map((section, index) => <div key={index} data-id="element-62">
                        <h3 className="text-lg font-serif font-bold text-[#1e2a4a] mb-2" data-id="element-63">
                          {section.title}
                        </h3>
                        <p className="text-[#1e2a4a]/70 text-sm leading-relaxed" data-id="element-64">
                          {section.content}
                        </p>
                      </div>)}
                  </div>

                  <div className="mt-8 pt-8 border-t border-[#c9a96e]/20 text-center" data-id="element-65">
                    <button onClick={() => setActiveModal(null)} className="text-[#6b7fa3] hover:text-[#1e2a4a] text-sm font-medium transition-colors" data-id="element-66">
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </>;
}